
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "@/hooks/useUserRole";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

function getReadableRows(vehicleData: any) {
  if (!vehicleData) return [];

  // For the main vehicle data (not looking at allMatches array)
  let logicalRows: { label: string; value: string }[] = [];
  
  if (typeof vehicleData === 'object' && vehicleData !== null) {
    // Filter out the rawResponse and allMatches to display separately
    const { rawResponse, allMatches, ...mainData } = vehicleData;
    
    const processObject = (obj: any, prefix = "") => {
      for (const key of Object.keys(obj)) {
        const value = obj[key];
        if (typeof value === "object" && value !== null && !Array.isArray(value)) {
          // Nested - flatten one level with prefixed key names
          processObject(value, prefix ? `${prefix}.${key}` : key);
        } else if (Array.isArray(value)) {
          // For arrays, try to display them sensibly
          logicalRows.push({ 
            label: prefix ? `${prefix}.${key}` : key, 
            value: Array.isArray(value) 
              ? JSON.stringify(value) 
              : String(value) 
          });
        } else {
          logicalRows.push({ 
            label: prefix ? `${prefix}.${key}` : key, 
            value: value == null ? "" : String(value) 
          });
        }
      }
    };
    
    processObject(mainData);
  }
  
  return logicalRows;
}

const MotorApi = () => {
  const { isAdmin, loading } = useUserRole();
  const navigate = useNavigate();
  const [vinNumber, setVinNumber] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [motorData, setMotorData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState("logical");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && !isAdmin()) {
      toast.error("You don't have permission to access this page");
      navigate("/");
    }
  }, [loading, isAdmin, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate VIN - must be exactly 17 characters
    if (!vinNumber || vinNumber.length !== 17) {
      toast.error("Please enter a valid 17-character VIN");
      return;
    }

    setIsProcessing(true);
    setMotorData(null);
    
    try {
      // Call the Supabase Edge Function
      console.log("Calling motor-vin-lookup edge function with VIN:", vinNumber);
      
      const { data, error } = await supabase.functions.invoke('motor-vin-lookup', {
        body: JSON.stringify({ vin: vinNumber }),
      });
      
      console.log("Edge function response:", { data, error });
      
      if (error) {
        console.error("Edge function error:", error);
        throw new Error(error.message || "Failed to lookup VIN");
      }

      if (!data) {
        toast.error("Empty response from MOTOR API");
        setError("Empty response received from the VIN lookup service");
        setIsProcessing(false);
        return;
      }

      setMotorData(data);
      toast.success(data.message || "VIN lookup successful!");
    } catch (error: any) {
      console.error("Error processing request:", error);
      
      let errorMessage = "Failed to process VIN lookup";
      if (error.message?.includes("404")) {
        errorMessage = "Edge function not found. Please make sure it's deployed correctly.";
      } else if (error.message?.includes("403")) {
        errorMessage = "Not authorized to access the edge function. Please check your authentication.";
      }
      
      toast.error(errorMessage);
      setError(errorMessage);
      setMotorData({ error: error?.message || String(error) });
    }
    
    setIsProcessing(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin()) {
    return null;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">MOTOR VIN Lookup</h1>
      
      <Alert className="bg-amber-50 border-amber-200">
        <AlertTitle>MOTOR DaaS API Integration</AlertTitle>
        <AlertDescription>
          This page connects to the MOTOR DaaS API using the sandbox credentials. 
          Enter a valid 17-character VIN to retrieve vehicle data.
        </AlertDescription>
      </Alert>
      
      {error && (
        <Alert variant="destructive" className="bg-red-50 border-red-200">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{error}</p>
            <p className="text-sm">Try one of these VINs from the MOTOR sandbox: 1FTSW2BR0AEB13613, WBASP2C58BC337294</p>
          </AlertDescription>
        </Alert>
      )}
      
      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="vin" className="block text-sm font-medium">
              Enter VIN Number
            </label>
            
            <div className="flex gap-2">
              <Input
                id="vin"
                value={vinNumber}
                onChange={(e) => setVinNumber(e.target.value.toUpperCase())}
                placeholder="Enter 17-character VIN (e.g., 1FTSW2BR0AEB13613)"
                maxLength={17}
                className="flex-1"
              />
              <Button
                type="submit"
                disabled={isProcessing || vinNumber.length !== 17}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            </div>
          </div>
        </form>
      </Card>

      {motorData && (
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full max-w-full"
        >
          <TabsList className="mb-2 w-full max-w-xl">
            <TabsTrigger value="logical">Logical View</TabsTrigger>
            <TabsTrigger value="raw">Raw JSON View</TabsTrigger>
          </TabsList>
          <TabsContent value="logical" className="w-full">
            <Card className="p-0 w-full max-w-full">
              <ScrollArea className="max-h-[70vh] max-w-full px-6 py-4">
                {motorData?.error ? (
                  <div className="text-red-600">{motorData.error}</div>
                ) : (
                  <>
                    {motorData.message && (
                      <div className="mb-4 p-3 bg-green-50 text-green-800 rounded border border-green-200">
                        {motorData.message}
                      </div>
                    )}

                    <div className="mb-6">
                      <h3 className="text-lg font-semibold mb-2">Primary Vehicle Details</h3>
                      <table className="min-w-full text-sm">
                        <tbody>
                          {getReadableRows(motorData.data)?.map((row, idx) => (
                            <tr
                              key={idx}
                              className="border-b last:border-b-0"
                            >
                              <td className="py-1 pr-4 font-medium text-gray-700 align-top w-56 max-w-[40vw] break-words">
                                {row.label}
                              </td>
                              <td className="py-1 text-[15px] text-gray-900 align-top break-words max-w-[60vw]">
                                {row.value}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {motorData && motorData.data?.allMatches && motorData.data.allMatches.length > 1 && (
                      <div className="mt-6">
                        <Accordion type="single" collapsible className="w-full">
                          <AccordionItem value="item-1">
                            <AccordionTrigger>
                              All Vehicle Matches ({motorData.data.allMatches.length})
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="h-[300px] overflow-hidden">
                                <ScrollArea className="h-full w-full pr-4">
                                  <div className="space-y-4">
                                    {motorData.data.allMatches.map((match: any, idx: number) => (
                                      <div key={idx} className="p-3 border rounded-md">
                                        <h4 className="font-medium mb-2">
                                          {match.year} {match.make} {match.model} {match.subModel}
                                        </h4>
                                        <div className="text-sm">
                                          <p><span className="font-medium">Engine:</span> {match.engineDescription}</p>
                                          <p><span className="font-medium">Vehicle ID:</span> {match.vehicleId}</p>
                                          <p><span className="font-medium">Base Vehicle ID:</span> {match.baseVehicleId}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </ScrollArea>
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      </div>
                    )}
                  </>
                )}
              </ScrollArea>
            </Card>
          </TabsContent>
          <TabsContent value="raw" className="w-full">
            <Card className="p-0 w-full max-w-full">
              <ScrollArea className="max-h-[70vh] max-w-full px-6 py-4">
                <Textarea
                  readOnly
                  value={JSON.stringify(motorData, null, 2)}
                  className="w-full text-xs font-mono bg-gray-50 border-none min-h-[300px] resize-none max-h-[65vh]"
                  style={{
                    minWidth: 0,
                    width: "100%",
                    whiteSpace: "pre-wrap",
                  }}
                />
              </ScrollArea>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default MotorApi;
